import React, { useState, useEffect, useRef } from 'react';
import './Checkout.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useToast } from "@/hooks/use-toast";
import { MapPin, ShoppingBag, CreditCard, Trash2, Plus, Minus, ArrowLeft, CheckCircle } from 'lucide-react';
import { BackendError, backendGet, backendPut } from '@/lib/backendClient';
import { createOrder } from '@/services/orders';
import {
  createMercadoPagoInlinePayment,
  createMercadoPagoPreference,
  getMercadoPagoConfig,
  listMercadoPagoSavedCards,
  startPaymentCheckout,
} from '@/services/payments';
import { reverseGeocode } from '@/services/routes';
import AddressMapPicker from '@/components/AddressMapPicker';
import { getDisplayedBasePriceReais, getStandardProductPriceReais } from '@/lib/pricing';
import { getSelectedExtrasUnitTotalReais } from '@/lib/extraPricing';
import { openExternalUrl } from '@/mobile/navigation';

const GEOCODE_REFRESH_MS = Number(process.env.REACT_APP_GEOCODE_REFRESH_MS) || 15000;

function parseCoord(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parseMoneyInputToReais(value) {
  const normalized = String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDistanceKm(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '-';
  return `${parsed.toFixed(1).replace('.', ',')} km`;
}

const VOUCHER_APPROVAL_APPROVED = 'approved';
const VOUCHER_ALLOWED_ISSUERS = new Set(['ticket', 'pluxee', 'biq']);
const VOUCHER_ALLOWED_TYPES = new Set(['alimentacao', 'refeicao']);
const MERCADOPAGO_SDK_URL = 'https://sdk.mercadopago.com/js/v2';

let mercadoPagoSdkPromise = null;

function normalizeVoucherToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeVoucherIssuers(value) {
  if (!Array.isArray(value)) return [];
  const unique = [];
  const seen = new Set();
  value.forEach((entry) => {
    const token = normalizeVoucherToken(entry);
    if (!VOUCHER_ALLOWED_ISSUERS.has(token)) return;
    if (seen.has(token)) return;
    seen.add(token);
    unique.push(token);
  });
  return unique;
}

function normalizeVoucherTypes(value) {
  if (!Array.isArray(value)) return [];
  const unique = [];
  const seen = new Set();
  value.forEach((entry) => {
    const token = normalizeVoucherToken(entry);
    if (!VOUCHER_ALLOWED_TYPES.has(token)) return;
    if (seen.has(token)) return;
    seen.add(token);
    unique.push(token);
  });
  return unique;
}

function resolveVoucherSettingsFromRestaurant(data) {
  const direct = data?.voucher && typeof data.voucher === 'object' ? data.voucher : null;
  const openingHoursSettings =
    data?.opening_hours?.voucher_settings && typeof data.opening_hours.voucher_settings === 'object'
      ? data.opening_hours.voucher_settings
      : null;
  const source = direct || openingHoursSettings || {};

  const acceptedIssuers = normalizeVoucherIssuers(source.accepted_issuers);
  const acceptedTypes = normalizeVoucherTypes(source.accepted_types);
  const approvalStatus = String(source.approval_status || '').trim().toLowerCase();
  const acceptsVoucher = Boolean(source.accepts_voucher);
  const explicitEnabled = source.enabled_for_checkout;
  const enabledForCheckout = typeof explicitEnabled === 'boolean'
    ? explicitEnabled
    : Boolean(
      acceptsVoucher
      && approvalStatus === VOUCHER_APPROVAL_APPROVED
      && acceptedIssuers.length > 0
      && acceptedTypes.length > 0
    );
  const partnerStoreId = String(source.partner_store_id || '').trim();

  return {
    enabled_for_checkout: enabledForCheckout,
    approval_status: approvalStatus || 'pending',
    accepts_voucher: acceptsVoucher,
    accepted_issuers: acceptedIssuers,
    accepted_types: acceptedTypes,
    partner_store_id: partnerStoreId || null,
  };
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function buildSavedCardKey(card, index) {
  const providerCustomerId = String(card?.provider_customer_id || '').trim();
  const providerCardId = String(card?.provider_card_id || '').trim();
  return `${providerCustomerId || 'customer'}:${providerCardId || 'card'}:${index}`;
}

function toFiniteNumberOrNull(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getInlineProviderStatusCode(error) {
  if (!(error instanceof BackendError)) return null;
  const detail = error.originalError?.detail;
  if (!detail || typeof detail !== 'object') return null;
  return toFiniteNumberOrNull(detail.provider_status_code);
}

function shouldUseCheckoutProFallbackForInlineError(error) {
  if (!error || error.noFallback === true) return false;
  if (error.allowCheckoutProFallback === true) return true;
  if (!(error instanceof BackendError)) return false;

  const backendStatusCode = toFiniteNumberOrNull(error.statusCode);
  if (backendStatusCode === null) return true;

  if ([0, 408, 503, 504].includes(backendStatusCode)) return true;
  if (backendStatusCode < 500) return false;

  const providerStatusCode = getInlineProviderStatusCode(error);
  if (providerStatusCode === null) return true;
  if ([0, 408, 503, 504].includes(providerStatusCode)) return true;
  return providerStatusCode >= 500;
}

function resolveCheckoutErrorMessage(error) {
  if (!error) return 'Tente novamente.';

  if (error instanceof BackendError) {
    const detail = error.originalError?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();

    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === 'string' && first.trim()) return first.trim();
      if (first && typeof first === 'object' && typeof first.msg === 'string' && first.msg.trim()) {
        return first.msg.trim();
      }
    }

    if (detail && typeof detail === 'object') {
      const direct =
        (typeof detail.message === 'string' && detail.message.trim())
        || (typeof detail.detail === 'string' && detail.detail.trim())
        || (typeof detail.error === 'string' && detail.error.trim())
        || '';
      if (direct) return direct;
    }

    const friendly = typeof error.getUserFriendlyMessage === 'function' ? error.getUserFriendlyMessage() : '';
    if (typeof friendly === 'string' && friendly.trim()) return friendly.trim();
  }

  if (error instanceof Error) {
    const message = String(error.message || '').trim();
    if (message && message !== '[object Object]') return message;
  }

  if (typeof error === 'string' && error.trim()) return error.trim();
  return 'Nao foi possivel finalizar o pedido no momento.';
}

function loadMercadoPagoSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Mercado Pago SDK indisponivel neste ambiente.'));
  }
  if (window.MercadoPago) {
    return Promise.resolve(window.MercadoPago);
  }
  if (mercadoPagoSdkPromise) {
    return mercadoPagoSdkPromise;
  }

  mercadoPagoSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-sdk="mercadopago-v2"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.MercadoPago));
      existing.addEventListener('error', () => reject(new Error('Falha ao carregar SDK do Mercado Pago.')));
      return;
    }

    const script = document.createElement('script');
    script.src = MERCADOPAGO_SDK_URL;
    script.async = true;
    script.defer = true;
    script.dataset.sdk = 'mercadopago-v2';
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => reject(new Error('Falha ao carregar SDK do Mercado Pago.'));
    document.body.appendChild(script);
  });

  return mercadoPagoSdkPromise;
}

export default function Checkout() {
  const { user } = useAuth();
  const { items, restaurantId, restaurantName, increaseQuantity, decreaseQuantity, removeItem, clearCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [address, setAddress] = useState({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: '',
    lat: null,
    lng: null
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);
  const [fulfillmentType, setFulfillmentType] = useState('');

  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [cashNeedsChange, setCashNeedsChange] = useState('');
  const [cashBillAmount, setCashBillAmount] = useState('');
  const [errors, setErrors] = useState({});
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryDistanceKm, setDeliveryDistanceKm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState('');
  const [autoAddressLabel, setAutoAddressLabel] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [voucherSettings, setVoucherSettings] = useState({
    enabled_for_checkout: false,
    approval_status: 'pending',
    accepts_voucher: false,
    accepted_issuers: [],
    accepted_types: [],
    partner_store_id: null,
  });
  const [storeAcceptsCash, setStoreAcceptsCash] = useState(true);
  const [mpConfig, setMpConfig] = useState({
    inlineEnabled: false,
    inlineFallbackToCheckoutPro: true,
    publicKey: '',
    supportedMethods: [],
  });
  const [savedMpCards, setSavedMpCards] = useState([]);
  const [inlineCardForm, setInlineCardForm] = useState({
    holderName: '',
    number: '',
    expMonth: '',
    expYear: '',
    cvv: '',
    documentType: 'CPF',
    documentNumber: '',
    saveCard: true,
  });
  const [selectedSavedCardKey, setSelectedSavedCardKey] = useState('');
  const [inlinePaymentInfo, setInlinePaymentInfo] = useState('');
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const isCompletingOrderRef = useRef(false);
  const emptyCartRedirectedRef = useRef(false);
  const reverseRequestRef = useRef({ key: '', ts: 0 });
  const checkoutIdempotencyRef = useRef('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (items.length > 0 || isCompletingOrder || orderSuccess) {
      emptyCartRedirectedRef.current = false;
      return;
    }

    if (items.length === 0 && !isCompletingOrder && !orderSuccess) {
      if (isCompletingOrderRef.current || emptyCartRedirectedRef.current) return;
      emptyCartRedirectedRef.current = true;
      toast({
        variant: "destructive",
        title: "Carrinho vazio",
        description: "Adicione itens ao carrinho antes de continuar.",
      });
      navigate('/');
      return;
    }
  }, [user, items.length, isCompletingOrder, orderSuccess, navigate, toast]);

  useEffect(() => {
    let isMounted = true;
    if (!user?.id) return () => { isMounted = false; };

    loadUserProfile(isMounted);
    loadMercadoPagoCheckoutConfig(isMounted);

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  async function loadRestaurantInfo(isMounted = true) {
    if (!restaurantId) return;

    try {
      const lat = parseCoord(address.lat);
      const lng = parseCoord(address.lng);
      const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
      const query = hasCoords
        ? `?lat=${encodeURIComponent(lat.toFixed(6))}&lng=${encodeURIComponent(lng.toFixed(6))}`
        : '';
      const data = await backendGet(`/api/restaurants/${restaurantId}${query}`);

      if (!isMounted) return;

      if (data) {
        setDeliveryFee(data.delivery_fee || 0);
        const parsedDistance = Number(data.delivery_distance_km);
        setDeliveryDistanceKm(Number.isFinite(parsedDistance) ? parsedDistance : null);
        setVoucherSettings(resolveVoucherSettingsFromRestaurant(data));
        setStoreAcceptsCash(data.accepts_cash !== false);
      }
    } catch (err) {
      if (isMounted) {
        console.error('Erro ao carregar taxa de entrega:', err);
        setVoucherSettings({
          enabled_for_checkout: false,
          approval_status: 'pending',
          accepts_voucher: false,
          accepted_issuers: [],
          accepted_types: [],
          partner_store_id: null,
        });
        setStoreAcceptsCash(true);
      }
    }
  }

  async function loadMercadoPagoCheckoutConfig(isMounted = true) {
    try {
      const configResponse = await getMercadoPagoConfig();
      if (!isMounted) return;

      const inlineEnabled = Boolean(configResponse?.inline_enabled);
      const publicKey = String(configResponse?.public_key || '').trim();
      const supportedMethods = Array.isArray(configResponse?.inline_supported_methods)
        ? configResponse.inline_supported_methods.map((method) => String(method || '').trim().toLowerCase()).filter(Boolean)
        : [];

      setMpConfig({
        inlineEnabled: inlineEnabled && Boolean(publicKey),
        inlineFallbackToCheckoutPro: Boolean(configResponse?.inline_fallback_to_checkout_pro ?? true),
        publicKey,
        supportedMethods,
      });

      if (inlineEnabled && publicKey) {
        const savedCardsResponse = await listMercadoPagoSavedCards();
        if (!isMounted) return;
        const cards = Array.isArray(savedCardsResponse?.cards) ? savedCardsResponse.cards : [];
        setSavedMpCards(cards);
        return;
      }

      setSavedMpCards([]);
    } catch (error) {
      if (!isMounted) return;
      console.warn('Falha ao carregar configuracao do checkout Mercado Pago:', error);
      setMpConfig({
        inlineEnabled: false,
        inlineFallbackToCheckoutPro: true,
        publicKey: '',
        supportedMethods: [],
      });
      setSavedMpCards([]);
    }
  }

  useEffect(() => {
    let isMounted = true;

    if (!restaurantId) return () => { isMounted = false; };

    loadRestaurantInfo(isMounted);

    return () => {
      isMounted = false;
    };
  }, [restaurantId, fulfillmentType, address.lat, address.lng]);

  async function loadUserProfile(isMounted = true) {
    try {
      const data = await backendGet('/api/auth/profile');

      if (!isMounted) return;

      setProfilePhone(data?.phone || '');

      const normalizedAddresses = Array.isArray(data?.addresses)
        ? data.addresses.filter((item) => item && typeof item === 'object')
        : (data?.address && typeof data.address === 'object' ? [data.address] : []);

      let primaryIndex = normalizedAddresses.findIndex((item) => item.is_primary === true);
      if (primaryIndex < 0 && normalizedAddresses.length > 0) primaryIndex = 0;

      setSavedAddresses(normalizedAddresses);
      setSelectedAddressIndex(primaryIndex >= 0 ? primaryIndex : 0);

      const loaded = primaryIndex >= 0 ? normalizedAddresses[primaryIndex] : {};
      setAddress({
        street: loaded.street || '',
        number: loaded.number || '',
        complement: loaded.complement || '',
        neighborhood: loaded.neighborhood || '',
        city: loaded.city || '',
        state: loaded.state || '',
        zipcode: loaded.zipcode || '',
        lat: parseCoord(loaded.lat),
        lng: parseCoord(loaded.lng)
      });

    } catch (err) {
      if (isMounted) {
        console.error('Erro ao carregar endereco/perfil:', err);
      }
    }
  }

  function selectSavedAddress(index) {
    const selected = savedAddresses[index];
    if (!selected) return;

    setSelectedAddressIndex(index);
    setAddress({
      street: selected.street || '',
      number: selected.number || '',
      complement: selected.complement || '',
      neighborhood: selected.neighborhood || '',
      city: selected.city || '',
      state: selected.state || '',
      zipcode: selected.zipcode || '',
      lat: parseCoord(selected.lat),
      lng: parseCoord(selected.lng)
    });
  }

  async function persistCustomerProfile() {
    const profilePayload = { ...address };

    try {
      if (savedAddresses.length > 0) {
        const nextAddresses = savedAddresses.map((item, idx) => ({
          ...item,
          ...(idx === selectedAddressIndex ? profilePayload : {}),
          is_primary: idx === selectedAddressIndex,
        }));

        await backendPut('/api/auth/profile', {
          addresses: nextAddresses,
          address: nextAddresses[selectedAddressIndex] || nextAddresses[0] || null,
        });
      } else {
        await backendPut('/api/auth/profile', {
          address: {
            ...profilePayload,
            is_primary: true,
          }
        });
      }
    } catch (error) {
      console.error('Erro ao salvar perfil do cliente:', error);
    }
  }

  const getItemUnitPrice = (item) => {
    const basePrice = getDisplayedBasePriceReais(item);
    const extrasUnit = getSelectedExtrasUnitTotalReais(item?.selected_extras);

    return basePrice + extrasUnit;
  };

  const getItemOriginalUnitPrice = (item) => {
    const basePrice = getStandardProductPriceReais(item);
    const extrasUnit = getSelectedExtrasUnitTotalReais(item?.selected_extras);

    return basePrice + extrasUnit;
  };

  const getItemRemovalNote = (item) => String(item?.removal_note || item?.removed_ingredients || '').trim();

  const buildCartAdjustmentsSummary = (sourceItems = []) => {
    const lines = (Array.isArray(sourceItems) ? sourceItems : [])
      .map((item) => {
        const removal = getItemRemovalNote(item);
        if (!removal) return '';
        const name = String(item?.name || item?.product_name || 'Item').trim();
        return `${name}: retirar ${removal}`;
      })
      .filter(Boolean);

    if (lines.length === 0) return '';
    return `Ajustes por item:
${lines.map((line) => `- ${line}`).join('\n')}`;
  };

  const checkoutSubtotal = Array.isArray(items)
    ? items.reduce((sum, item) => sum + (getItemUnitPrice(item) * (item.quantity || 0)), 0)
    : 0;
  const checkoutItemsCount = Array.isArray(items)
    ? items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
    : 0;
  const checkoutOriginalSubtotal = Array.isArray(items)
    ? items.reduce((sum, item) => sum + (getItemOriginalUnitPrice(item) * (item.quantity || 0)), 0)
    : 0;
  const checkoutDiscount = Math.max(0, checkoutOriginalSubtotal - checkoutSubtotal);

  const effectiveDeliveryFee = fulfillmentType === 'pickup' ? 0 : deliveryFee;
  const total = checkoutSubtotal + effectiveDeliveryFee;
  const parsedCashBillReais = parseMoneyInputToReais(cashBillAmount);
  const hasValidCashBill = Number.isFinite(parsedCashBillReais) && parsedCashBillReais > 0;
  const voucherEnabledForCheckout = voucherSettings.enabled_for_checkout === true;
  const isMercadoPagoMethod = paymentMethod === 'pix' || paymentMethod === 'credit_card' || paymentMethod === 'debit_card';
  const isMercadoPagoCardMethod = paymentMethod === 'credit_card' || paymentMethod === 'debit_card';
  const isInlineSupportedForMethod = mpConfig.inlineEnabled
    && isMercadoPagoCardMethod
    && (
      mpConfig.supportedMethods.length === 0
      || mpConfig.supportedMethods.includes(paymentMethod)
    );
  const normalizedSavedMpCards = Array.isArray(savedMpCards)
    ? savedMpCards.filter((card) => (
      String(card?.provider_card_id || '').trim()
      || String(card?.provider_customer_id || '').trim()
    ))
    : [];
  const selectedSavedCard = normalizedSavedMpCards.find(
    (card, index) => buildSavedCardKey(card, index) === selectedSavedCardKey,
  ) || null;
  const usingSavedCardInline = Boolean(
    isInlineSupportedForMethod
    && selectedSavedCard
    && String(selectedSavedCard.provider_card_id || '').trim(),
  );
  const inlineCardNumberDigits = digitsOnly(inlineCardForm.number);
  const inlineCardCvvDigits = digitsOnly(inlineCardForm.cvv);
  const inlineCardDocDigits = digitsOnly(inlineCardForm.documentNumber);

  useEffect(() => {
    if (paymentMethod === 'cash') return;
    setCashNeedsChange('');
    setCashBillAmount('');
    setErrors((prev) => {
      if (!prev.cashNeedsChange && !prev.cashBillAmount) return prev;
      const next = { ...prev };
      delete next.cashNeedsChange;
      delete next.cashBillAmount;
      return next;
    });
  }, [paymentMethod]);

  useEffect(() => {
    if (paymentMethod === 'voucher' && !voucherEnabledForCheckout) {
      setPaymentMethod('credit_card');
    }
  }, [paymentMethod, voucherEnabledForCheckout]);

  useEffect(() => {
    if (paymentMethod === 'cash' && !storeAcceptsCash) {
      setPaymentMethod(voucherEnabledForCheckout ? 'voucher' : 'credit_card');
    }
  }, [paymentMethod, storeAcceptsCash, voucherEnabledForCheckout]);

  useEffect(() => {
    if (!selectedSavedCardKey) return;
    const stillExists = normalizedSavedMpCards.some(
      (card, index) => buildSavedCardKey(card, index) === selectedSavedCardKey,
    );
    if (!stillExists) {
      setSelectedSavedCardKey('');
    }
  }, [savedMpCards, selectedSavedCardKey]);

  useEffect(() => {
    if (isInlineSupportedForMethod) return;
    setSelectedSavedCardKey('');
    setInlinePaymentInfo('');
    setErrors((prev) => {
      const keys = [
        'inlineHolderName',
        'inlineCardNumber',
        'inlineExpMonth',
        'inlineExpYear',
        'inlineCvv',
        'inlineDocumentNumber',
      ];
      const hasAny = keys.some((key) => prev[key]);
      if (!hasAny) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  }, [isInlineSupportedForMethod]);

  useEffect(() => {
    if (!isInlineSupportedForMethod) return;
    setErrors((prev) => {
      const keysToClear = usingSavedCardInline
        ? ['inlineHolderName', 'inlineCardNumber', 'inlineExpMonth', 'inlineExpYear']
        : ['inlineSavedCard'];
      const hasAny = keysToClear.some((key) => prev[key]);
      if (!hasAny) return prev;
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });
  }, [isInlineSupportedForMethod, usingSavedCardInline]);

  function validateForm() {
    const newErrors = {};

    if (!fulfillmentType) {
      newErrors.fulfillmentType = 'Selecione se quer retirar na loja ou entrega.';
    }

    if (fulfillmentType === 'delivery') {
      if (!address.street.trim()) newErrors.street = 'Rua e obrigatoria';
      if (!address.number.trim()) newErrors.number = 'Numero e obrigatorio';
      if (!address.neighborhood.trim()) newErrors.neighborhood = 'Bairro e obrigatorio';
      if (!address.city.trim()) newErrors.city = 'Cidade e obrigatoria';
      if (!address.state.trim()) newErrors.state = 'Estado e obrigatorio';
      if (!Number.isFinite(address.lat) || !Number.isFinite(address.lng)) {
        newErrors.location = 'Selecione sua localizacao no mapa.';
      }

      const zipcodeRegex = /^\d{5}-?\d{3}$/;
      if (!address.zipcode.trim()) {
        newErrors.zipcode = 'CEP e obrigatorio';
      } else if (!zipcodeRegex.test(address.zipcode)) {
        newErrors.zipcode = 'CEP invalido';
      }

      const phoneDigits = String(profilePhone || '').replace(/\D/g, '');
      if (phoneDigits.length < 4) {
        newErrors.phone = 'Telefone obrigatorio para confirmar a entrega. Atualize no Perfil.';
      }
    }

    if (paymentMethod === 'cash') {
      if (!storeAcceptsCash) {
        newErrors.paymentMethod = 'Esta loja nao aceita pagamento em dinheiro.';
      }
      if (cashNeedsChange !== 'yes' && cashNeedsChange !== 'no') {
        newErrors.cashNeedsChange = 'Informe se precisa de troco.';
      }

      if (cashNeedsChange === 'yes') {
        if (!hasValidCashBill) {
          newErrors.cashBillAmount = 'Informe o valor da nota.';
        } else if (parsedCashBillReais < total) {
          newErrors.cashBillAmount = 'Valor da nota deve ser maior ou igual ao total do pedido.';
        }
      }
    }

    if (isInlineSupportedForMethod) {
      if (usingSavedCardInline) {
        if (!selectedSavedCard) {
          newErrors.inlineSavedCard = 'Selecione um cartao salvo ou use um novo cartao.';
        }
      } else {
        if (!inlineCardForm.holderName.trim()) {
          newErrors.inlineHolderName = 'Informe o nome impresso no cartao.';
        }
        if (inlineCardNumberDigits.length < 13 || inlineCardNumberDigits.length > 19) {
          newErrors.inlineCardNumber = 'Numero de cartao invalido.';
        }
        const month = Number(inlineCardForm.expMonth);
        if (!Number.isInteger(month) || month < 1 || month > 12) {
          newErrors.inlineExpMonth = 'Mes de expiracao invalido.';
        }
        const year = Number(inlineCardForm.expYear);
        if (!Number.isInteger(year) || String(inlineCardForm.expYear).trim().length !== 4) {
          newErrors.inlineExpYear = 'Ano de expiracao invalido.';
        }
      }
      if (inlineCardCvvDigits.length < 3 || inlineCardCvvDigits.length > 4) {
        newErrors.inlineCvv = 'CVV invalido.';
      }
      if (inlineCardDocDigits.length < 11) {
        newErrors.inlineDocumentNumber = 'Documento do titular obrigatorio.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function getCheckoutIdempotencyKey() {
    if (checkoutIdempotencyRef.current) return checkoutIdempotencyRef.current;
    const cryptoId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null;
    checkoutIdempotencyRef.current =
      cryptoId || `checkout_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    return checkoutIdempotencyRef.current;
  }

  function handleAddressChange(field, value) {
    setAddress(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }

  function handleInlineCardFieldChange(field, value) {
    setInlineCardForm((prev) => ({ ...prev, [field]: value }));
    const errorKeyByField = {
      holderName: 'inlineHolderName',
      number: 'inlineCardNumber',
      expMonth: 'inlineExpMonth',
      expYear: 'inlineExpYear',
      cvv: 'inlineCvv',
      documentNumber: 'inlineDocumentNumber',
    };
    const targetError = errorKeyByField[field];
    if (!targetError) return;
    setErrors((prev) => {
      if (!prev[targetError]) return prev;
      const next = { ...prev };
      delete next[targetError];
      return next;
    });
  }

  function handleSelectSavedCard(cardKey) {
    setSelectedSavedCardKey((prev) => (prev === cardKey ? '' : cardKey));
    setInlinePaymentInfo('');
    setErrors((prev) => {
      const keys = ['inlineSavedCard', 'inlineHolderName', 'inlineCardNumber', 'inlineExpMonth', 'inlineExpYear'];
      const hasAny = keys.some((key) => prev[key]);
      if (!hasAny) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  }

  async function resolveMercadoPagoPaymentMethodId(mp, cardNumberDigits) {
    const bin = String(cardNumberDigits || '').slice(0, 8);
    if (!bin || bin.length < 6) {
      throw new Error('Numero de cartao invalido para identificar bandeira.');
    }

    const response = await mp.getPaymentMethods({ bin });
    const methods = Array.isArray(response?.results)
      ? response.results
      : (Array.isArray(response?.body?.results) ? response.body.results : []);
    const first = methods[0] || null;
    const paymentMethodId = String(first?.id || '').trim();
    const issuerId = first?.issuer?.id ?? first?.issuer_id ?? null;

    if (!paymentMethodId) {
      throw new Error('Nao foi possivel identificar a bandeira do cartao.');
    }

    return {
      paymentMethodId,
      issuerId: issuerId != null ? String(issuerId) : null,
    };
  }

  async function tokenizeInlineCardWithMercadoPago() {
    if (!mpConfig.publicKey) {
      throw new Error('Configuracao do Mercado Pago indisponivel para checkout inline.');
    }

    try {
      await loadMercadoPagoSdk();
    } catch (sdkErr) {
      const technicalError = new Error(
        sdkErr instanceof Error ? sdkErr.message : 'Falha ao carregar SDK do Mercado Pago.',
      );
      technicalError.allowCheckoutProFallback = true;
      throw technicalError;
    }
    if (!window.MercadoPago) {
      const technicalError = new Error('SDK do Mercado Pago nao encontrado no navegador.');
      technicalError.allowCheckoutProFallback = true;
      throw technicalError;
    }

    const mp = new window.MercadoPago(mpConfig.publicKey, { locale: 'pt-BR' });
    if (usingSavedCardInline) {
      const providerCardId = String(selectedSavedCard?.provider_card_id || '').trim();
      if (!providerCardId) {
        throw new Error('Cartao salvo invalido para tokenizacao.');
      }

      const tokenPayload = {
        cardId: providerCardId,
        securityCode: inlineCardCvvDigits,
        identificationType: String(inlineCardForm.documentType || 'CPF').trim().toUpperCase(),
        identificationNumber: inlineCardDocDigits,
      };

      const tokenResponse = await mp.createCardToken(tokenPayload);
      const tokenId = String(tokenResponse?.id || tokenResponse?.response?.id || '').trim();
      if (!tokenId) {
        throw new Error('Nao foi possivel tokenizar o cartao salvo no Mercado Pago.');
      }

      const paymentMethodFromToken = String(
        tokenResponse?.payment_method_id
        || tokenResponse?.payment_method?.id
        || tokenResponse?.card?.payment_method_id
        || '',
      ).trim().toLowerCase();

      const paymentMethodId = String(
        selectedSavedCard?.brand
        || paymentMethodFromToken
        || '',
      ).trim().toLowerCase();

      if (!paymentMethodId) {
        throw new Error('Nao foi possivel identificar a bandeira do cartao salvo.');
      }

      return {
        tokenId,
        paymentMethodId,
        issuerId: null,
        usedSavedCard: true,
        providerCustomerId: String(selectedSavedCard?.provider_customer_id || '').trim() || null,
        providerCardId,
      };
    }

    const { paymentMethodId, issuerId } = await resolveMercadoPagoPaymentMethodId(mp, inlineCardNumberDigits);

    const tokenPayload = {
      cardNumber: inlineCardNumberDigits,
      cardExpirationMonth: String(inlineCardForm.expMonth || '').trim(),
      cardExpirationYear: String(inlineCardForm.expYear || '').trim(),
      securityCode: inlineCardCvvDigits,
      cardholderName: String(inlineCardForm.holderName || '').trim(),
      identificationType: String(inlineCardForm.documentType || 'CPF').trim().toUpperCase(),
      identificationNumber: inlineCardDocDigits,
    };

    const tokenResponse = await mp.createCardToken(tokenPayload);
    const tokenId = String(tokenResponse?.id || tokenResponse?.response?.id || '').trim();
    if (!tokenId) {
      throw new Error('Nao foi possivel tokenizar o cartao no Mercado Pago.');
    }

    return {
      tokenId,
      paymentMethodId,
      issuerId,
      usedSavedCard: false,
      providerCustomerId: null,
      providerCardId: null,
    };
  }

  async function reverseGeocodeAddress(lat, lng) {
    const roundedLat = Number(lat.toFixed(5));
    const roundedLng = Number(lng.toFixed(5));
    const key = `${roundedLat},${roundedLng}`;
    const now = Date.now();
    if (reverseRequestRef.current.key === key && (now - reverseRequestRef.current.ts) < GEOCODE_REFRESH_MS) {
      return;
    }

    reverseRequestRef.current = { key, ts: now };
    setReverseLoading(true);
    setReverseError('');

    try {
      const data = await reverseGeocode({ lat: roundedLat, lng: roundedLng });
      if (!data) return;

      setAutoAddressLabel(data.label || '');

      setAddress(prev => ({
        ...prev,
        lat: roundedLat,
        lng: roundedLng,
        street: data.street || prev.street,
        number: data.number || prev.number,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: (data.state_code && data.state_code.length <= 3 ? data.state_code : data.state) || prev.state,
        zipcode: data.zipcode || prev.zipcode,
      }));
    } catch (err) {
      setReverseError(err?.message || 'Nao foi possivel preencher o endereco automaticamente.');
    } finally {
      setReverseLoading(false);
    }
  }

  function handleLocationChange(nextLocation) {
    if (!nextLocation) return;
    const lat = Number(nextLocation.lat);
    const lng = Number(nextLocation.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    setAddress(prev => ({ ...prev, lat, lng }));
    if (errors.location) {
      setErrors(prev => ({ ...prev, location: null }));
    }

    reverseGeocodeAddress(lat, lng);
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Geolocalizacao nao suportada neste dispositivo.',
      });
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationChange({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocating(false);
      },
      () => {
        setLocating(false);
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'Nao foi possivel obter sua localizacao.',
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }

  async function handleCheckout(e) {
    e.preventDefault();

    if (items.length === 0) {
      toast({
        variant: "destructive",
        title: "Carrinho vazio",
        description: "Adicione itens ao carrinho antes de finalizar.",
      });
      return;
    }

    if (!validateForm()) {
      toast({
        variant: "destructive",
        title: "Erro de Validacao",
        description: "Por favor, selecione recebimento e preencha os campos obrigatorios.",
      });
      return;
    }

    setLoading(true);
    setIsCompletingOrder(true);
    isCompletingOrderRef.current = true;

    try {
      if (fulfillmentType === 'delivery') {
        await persistCustomerProfile();
      }

      const orderItems = items.map(item => ({
        product_id: item.id,
        product_name: item.name,
        product_price: getItemUnitPrice(item),
        quantity: item.quantity,
        selected_extras: Array.isArray(item.selected_extras) ? item.selected_extras : [],
        notes: getItemRemovalNote(item) || null
      }));

      const customizationsSummary = buildCartAdjustmentsSummary(items);
      const finalOrderNotes = [
        customizationsSummary,
      ]
        .filter(Boolean)
        .join('\n\n');
      const effectivePaymentMethod =
        paymentMethod === 'voucher' && !voucherEnabledForCheckout
          ? 'credit_card'
          : paymentMethod;
      if (effectivePaymentMethod === 'cash' && !storeAcceptsCash) {
        throw new Error('Esta loja nao aceita pagamento em dinheiro.');
      }
      const voucherPayload = effectivePaymentMethod === 'voucher'
        ? {
          voucher_issuer: voucherSettings.accepted_issuers[0] || 'unknown',
          voucher_type: voucherSettings.accepted_types[0] || 'unknown',
          voucher_brand: 'unknown',
          ...(voucherSettings.partner_store_id ? { merchant_voucher_account_id: voucherSettings.partner_store_id } : {}),
        }
        : null;

      const orderData = {
        restaurant_id: restaurantId,
        items: orderItems,
        delivery_address: fulfillmentType === 'delivery' ? { ...address, phone: profilePhone } : { type: 'pickup' },
        fulfillment_type: fulfillmentType,
        payment_method: effectivePaymentMethod,
        cash_change: effectivePaymentMethod === 'cash' ? {
          needs_change: cashNeedsChange === 'yes',
          bill_amount: cashNeedsChange === 'yes' && hasValidCashBill ? parsedCashBillReais : null,
        } : null,
        voucher: voucherPayload,
        notes: finalOrderNotes || null
      };

      const idempotencyKey = getCheckoutIdempotencyKey();
      const result = await createOrder(orderData, { idempotencyKey });

      const orderId = result.id || result.order_id;
      const isOnlinePayment = ['pix', 'credit_card', 'debit_card'].includes(effectivePaymentMethod);
      if (isOnlinePayment) {
        const shouldTryInlineCard = (
          effectivePaymentMethod === 'credit_card'
          || effectivePaymentMethod === 'debit_card'
        ) && isInlineSupportedForMethod;

        if (shouldTryInlineCard) {
          try {
            setInlinePaymentInfo('');
            const tokenized = await tokenizeInlineCardWithMercadoPago();
            const inlineResult = await createMercadoPagoInlinePayment(orderId, {
              token: tokenized.tokenId,
              payment_method_id: tokenized.paymentMethodId,
              issuer_id: tokenized.issuerId,
              installments: effectivePaymentMethod === 'debit_card' ? 1 : 1,
              payer_document_type: inlineCardForm.documentType || 'CPF',
              payer_document_number: inlineCardDocDigits,
              save_card: Boolean(inlineCardForm.saveCard && !tokenized.usedSavedCard),
              use_saved_card: Boolean(tokenized.usedSavedCard),
              provider_customer_id: tokenized.providerCustomerId,
              provider_card_id: tokenized.providerCardId,
            });
            const inlineStatus = String(inlineResult?.payment_status || '').trim().toLowerCase();
            if (inlineStatus === 'rejected' || inlineStatus === 'cancelled') {
              const statusDetail = String(inlineResult?.status_detail || '').trim();
              const baseMessage = inlineStatus === 'cancelled'
                ? 'Pagamento cancelado pelo emissor.'
                : 'Pagamento recusado pelo emissor.';
              const businessError = new Error(
                statusDetail ? `${baseMessage} (${statusDetail})` : `${baseMessage} Tente outro cartao.`,
              );
              businessError.noFallback = true;
              throw businessError;
            }

            if (inlineResult?.saved_card_reference?.last4) {
              setInlinePaymentInfo(`Cartao final ${inlineResult.saved_card_reference.last4} salvo para proximas compras.`);
            } else {
              setInlinePaymentInfo('Pagamento processado no checkout inline.');
            }

            setOrderSuccess({
              orderNumber: result.order_number,
              state: 'confirmed',
            });
            isCompletingOrderRef.current = false;
            setIsCompletingOrder(false);
            setLoading(false);
            return;
          } catch (inlineErr) {
            if (!mpConfig.inlineFallbackToCheckoutPro || !shouldUseCheckoutProFallbackForInlineError(inlineErr)) {
              throw inlineErr;
            }
            const inlineErrorMessage = inlineErr instanceof Error ? inlineErr.message : String(inlineErr || '');
            console.warn(`Falha tecnica no checkout inline. Tentando fallback Checkout Pro. Motivo: ${inlineErrorMessage}`);
          }
        }

        const preference = await createMercadoPagoPreference(orderId);
        const initPoint = preference?.init_point || preference?.sandbox_init_point;
        if (!initPoint) {
          throw new Error('Pedido criado, mas nao foi possivel iniciar o checkout online.');
        }

        await openExternalUrl(initPoint);
        return;
      }

      if (effectivePaymentMethod === 'voucher') {
        const checkoutResult = await startPaymentCheckout(orderId);
        const voucherStatus = String(checkoutResult?.voucher_status || '').trim().toLowerCase();
        if (voucherStatus === 'rejected' || voucherStatus === 'cancelled') {
          const message = voucherStatus === 'cancelled'
            ? 'Pagamento voucher cancelado. Escolha outra forma de pagamento.'
            : 'Pagamento voucher recusado. Escolha outra forma de pagamento.';
          throw new Error(message);
        }
        const voucherApproved = voucherStatus === 'approved' || voucherStatus === 'authorized';
        setOrderSuccess({
          orderNumber: result.order_number,
          state: voucherApproved ? 'confirmed' : 'pending_voucher',
        });
        isCompletingOrderRef.current = false;
        setIsCompletingOrder(false);
        setLoading(false);
        return;
      }

      setOrderSuccess({
        orderNumber: result.order_number,
        state: 'confirmed',
      });
      isCompletingOrderRef.current = false;
      setIsCompletingOrder(false);
      setLoading(false);
      return;

    } catch (err) {
      isCompletingOrderRef.current = false;
      setIsCompletingOrder(false);
      console.error('Erro ao finalizar pedido:', err);
      toast({
        variant: "destructive",
        title: "Erro ao finalizar pedido",
        description: resolveCheckoutErrorMessage(err),
      });
    } finally {
      if (!isCompletingOrderRef.current) {
        setLoading(false);
      }
    }
  }

  function handleGoToOrders() {
    if (!orderSuccess?.orderNumber) return;

    clearCart();
    navigate('/perfil?tab=orders', {
      replace: true,
      state: {
        orderCreated: true,
        orderNumber: orderSuccess.orderNumber,
      },
    });
  }

  const successState = String(orderSuccess?.state || 'confirmed').trim().toLowerCase();
  const isVoucherPendingApproval = successState === 'pending_voucher';
  const successTitle = isVoucherPendingApproval ? 'Pedido recebido' : 'Pedido confirmado';
  const successDescription = isVoucherPendingApproval
    ? `Pedido #${orderSuccess?.orderNumber} criado e aguardando autorizacao do voucher.`
    : `Pedido #${orderSuccess?.orderNumber} criado com sucesso.`;

  if (items.length === 0 && !isCompletingOrder && !orderSuccess) {
    return (
      <div className="checkout-container">
        <div className="empty-checkout">
          <ShoppingBag size={80} className="empty-icon" />
          <h2>Carrinho vazio</h2>
          <p>Adicione itens ao carrinho para continuar</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Ver Restaurantes
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="checkout-container">
      {orderSuccess && (
        <div className="checkout-success-overlay" role="status" aria-live="polite">
          <div className="checkout-success-card">
            <CheckCircle size={24} />
            <h3>{successTitle}</h3>
            <p>{successDescription}</p>
            <button type="button" className="checkout-success-action" onClick={handleGoToOrders}>
              Redirecionar para seus pedidos
            </button>
          </div>
        </div>
      )}
      <div className="checkout-header">
        <div className="checkout-header-inner">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Voltar
          </button>
          <div className="checkout-title-wrap">
            <h1 className="checkout-title">Finalizar Pedido</h1>
            <p className="checkout-subtitle">{restaurantName}</p>
            <div className="checkout-badges" aria-label="Resumo do checkout">
              <span className="checkout-badge">{checkoutItemsCount} {checkoutItemsCount === 1 ? 'item' : 'itens'}</span>
              <span className="checkout-badge highlight">Total estimado: R$ {total.toFixed(2).replace('.', ',')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="checkout-content">
        <div className="checkout-main">
          <section className="checkout-section">
            <div className="section-header">
              <ShoppingBag size={24} className="section-icon" />
              <h2>Como voce quer receber?</h2>
            </div>

            <div className="fulfillment-options">
              <label className={`payment-option ${fulfillmentType === 'delivery' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="fulfillment"
                  value="delivery"
                  checked={fulfillmentType === 'delivery'}
                  onChange={(e) => setFulfillmentType(e.target.value)}
                />
                <span>Entregar no meu endereco</span>
              </label>

              <label className={`payment-option ${fulfillmentType === 'pickup' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="fulfillment"
                  value="pickup"
                  checked={fulfillmentType === 'pickup'}
                  onChange={(e) => setFulfillmentType(e.target.value)}
                />
                <span>Retirar na loja</span>
              </label>
            </div>

            {errors.fulfillmentType && <span className="error-message">{errors.fulfillmentType}</span>}
          </section>

          <section className="checkout-section">
            <div className="section-header">
              <MapPin size={24} className="section-icon" />
              <h2>Endereco de Entrega</h2>
            </div>

            {errors.phone && (
              <span className="error-message">{errors.phone}</span>
            )}

            <form className="address-form">
              {savedAddresses.length > 0 && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Endereco salvo</label>
                    <select value={selectedAddressIndex} onChange={(e) => selectSavedAddress(Number(e.target.value))}>
                      {savedAddresses.map((addr, idx) => (
                        <option key={`${addr.label || 'Endereco'}-${idx}`} value={idx}>
                          {(addr.label || `Endereco ${idx + 1}`)}{addr.is_primary ? ' (Principal)' : ''} - {addr.street || 'Sem rua'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>CEP *</label>
                  <input
                    type="text"
                    placeholder="00000-000"
                    value={address.zipcode}
                    onChange={(e) => handleAddressChange('zipcode', e.target.value)}
                    className={errors.zipcode ? 'error' : ''}
                    maxLength="9"
                  />
                  {errors.zipcode && <span className="error-message">{errors.zipcode}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group flex-3">
                  <label>Rua *</label>
                  <input
                    type="text"
                    placeholder="Nome da rua"
                    value={address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    className={errors.street ? 'error' : ''}
                  />
                  {errors.street && <span className="error-message">{errors.street}</span>}
                </div>
                <div className="form-group flex-1">
                  <label>Numero *</label>
                  <input
                    type="text"
                    placeholder="123"
                    value={address.number}
                    onChange={(e) => handleAddressChange('number', e.target.value)}
                    className={errors.number ? 'error' : ''}
                  />
                  {errors.number && <span className="error-message">{errors.number}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Complemento</label>
                  <input
                    type="text"
                    placeholder="Apto, bloco, etc"
                    value={address.complement}
                    onChange={(e) => handleAddressChange('complement', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group flex-2">
                  <label>Bairro *</label>
                  <input
                    type="text"
                    placeholder="Nome do bairro"
                    value={address.neighborhood}
                    onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                    className={errors.neighborhood ? 'error' : ''}
                  />
                  {errors.neighborhood && <span className="error-message">{errors.neighborhood}</span>}
                </div>
                <div className="form-group flex-2">
                  <label>Cidade *</label>
                  <input
                    type="text"
                    placeholder="Cidade"
                    value={address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    className={errors.city ? 'error' : ''}
                  />
                  {errors.city && <span className="error-message">{errors.city}</span>}
                </div>
                <div className="form-group flex-1">
                  <label>UF *</label>
                  <input
                    type="text"
                    placeholder="SP"
                    value={address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase())}
                    className={errors.state ? 'error' : ''}
                    maxLength="2"
                  />
                  {errors.state && <span className="error-message">{errors.state}</span>}
                </div>
              </div>
            </form>

            {fulfillmentType === 'delivery' && (
              <div className="map-picker">
                <p className="map-helper">Clique no mapa para marcar sua localizacao exata.</p>
                <AddressMapPicker
                  value={{ lat: address.lat, lng: address.lng }}
                  onChange={handleLocationChange}
                  height={260}
                />
                <div className="map-picker-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleUseCurrentLocation}
                    disabled={locating}
                  >
                    {locating ? 'Localizando...' : 'Usar minha localizacao'}
                  </button>
                  <span className="map-helper">Se estiver em local fechado, aproxime o zoom e clique no ponto correto.</span>
                </div>
                {errors.location && <span className="error-message">{errors.location}</span>}
              {reverseLoading && <span className="map-helper">Buscando endereco automaticamente...</span>}
              {reverseError && <span className="error-message">{reverseError}</span>}
              {autoAddressLabel && !reverseLoading && !reverseError && (
                <span className="map-helper">Endereco sugerido: {autoAddressLabel}</span>
              )}
              </div>
            )}
          </section>

          <section className="checkout-section">
            <div className="section-header">
              <CreditCard size={24} className="section-icon" />
              <h2>Forma de Pagamento</h2>
            </div>

            <div className="payment-methods">
              <label className={`payment-option ${paymentMethod === 'credit_card' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="credit_card"
                  checked={paymentMethod === 'credit_card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>{'Cart\u00e3o de Cr\u00e9dito'}</span>
              </label>
              <label className={`payment-option ${paymentMethod === 'debit_card' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="debit_card"
                  checked={paymentMethod === 'debit_card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>{'Cart\u00e3o de D\u00e9bito'}</span>
              </label>
              <label className={`payment-option ${paymentMethod === 'pix' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="pix"
                  checked={paymentMethod === 'pix'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>PIX</span>
              </label>
              {voucherEnabledForCheckout && (
                <label className={`payment-option ${paymentMethod === 'voucher' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="voucher"
                    checked={paymentMethod === 'voucher'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>Voucher (VA/VR)</span>
                </label>
              )}
              {storeAcceptsCash && (
                <label className={`payment-option ${paymentMethod === 'cash' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>Dinheiro</span>
                </label>
              )}
            </div>
            {errors.paymentMethod && <span className="error-message">{errors.paymentMethod}</span>}

            {paymentMethod === 'cash' && (
              <div className="cash-change-box">
                <p className="cash-change-question">Levar troco?</p>
                <div className="cash-change-options">
                  <label className={`cash-change-option ${cashNeedsChange === 'yes' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="cash-needs-change"
                      value="yes"
                      checked={cashNeedsChange === 'yes'}
                      onChange={(e) => {
                        setCashNeedsChange(e.target.value);
                        setErrors((prev) => ({ ...prev, cashNeedsChange: null }));
                      }}
                    />
                    <span>Sim</span>
                  </label>
                  <label className={`cash-change-option ${cashNeedsChange === 'no' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="cash-needs-change"
                      value="no"
                      checked={cashNeedsChange === 'no'}
                      onChange={(e) => {
                        setCashNeedsChange(e.target.value);
                        setCashBillAmount('');
                        setErrors((prev) => ({ ...prev, cashNeedsChange: null, cashBillAmount: null }));
                      }}
                    />
                    <span>Nao</span>
                  </label>
                </div>
                {errors.cashNeedsChange && <span className="error-message">{errors.cashNeedsChange}</span>}

                {cashNeedsChange === 'yes' && (
                  <div className="cash-bill-group">
                    <label htmlFor="cashBillAmount">Valor da nota</label>
                    <input
                      id="cashBillAmount"
                      type="text"
                      inputMode="decimal"
                      placeholder="Ex.: 50,00"
                      value={cashBillAmount}
                      onChange={(e) => {
                        setCashBillAmount(e.target.value);
                        setErrors((prev) => ({ ...prev, cashBillAmount: null }));
                      }}
                      className={errors.cashBillAmount ? 'error' : ''}
                    />
                    {errors.cashBillAmount && <span className="error-message">{errors.cashBillAmount}</span>}
                  </div>
                )}
              </div>
            )}

            {isInlineSupportedForMethod && (
              <div className="inline-card-box">
                <p className="map-helper">
                  Cartao processado no checkout inline com tokenizacao segura do Mercado Pago. O CorreFood nao armazena PAN/CVV.
                </p>

                {normalizedSavedMpCards.length > 0 && (
                  <div className="inline-saved-cards">
                    <span className="inline-saved-cards-title">Cartoes ja vinculados</span>
                    <div className="inline-saved-cards-list">
                      <button
                        type="button"
                        className={`inline-saved-card-chip ${!usingSavedCardInline ? 'selected' : ''}`}
                        onClick={() => handleSelectSavedCard('')}
                      >
                        Usar novo cartao
                      </button>
                      {normalizedSavedMpCards.slice(0, 4).map((card, index) => {
                        const cardKey = buildSavedCardKey(card, index);
                        const selected = selectedSavedCardKey === cardKey;
                        const disabled = !String(card?.provider_card_id || '').trim();
                        return (
                          <button
                            type="button"
                            key={cardKey}
                            className={`inline-saved-card-chip ${selected ? 'selected' : ''}`}
                            onClick={() => handleSelectSavedCard(cardKey)}
                            disabled={disabled}
                            title={disabled ? 'Cartao salvo sem card_id, use um novo cartao' : undefined}
                          >
                            {(card.brand || 'cartao').toUpperCase()} **** {card.last4 || '----'}
                          </button>
                        );
                      })}
                    </div>
                    <span className="map-helper">
                      Referencias salvas ajudam a identificar o cartao. Numero completo e CVV nunca sao armazenados no CorreFood.
                    </span>
                    {errors.inlineSavedCard && <span className="error-message">{errors.inlineSavedCard}</span>}
                  </div>
                )}

                {usingSavedCardInline && selectedSavedCard ? (
                  <>
                    <p className="map-helper">
                      Cartao selecionado: {(selectedSavedCard.brand || 'cartao').toUpperCase()} final {selectedSavedCard.last4 || '----'}.
                    </p>
                    <div className="inline-card-grid">
                      <div className="form-group">
                        <label>CVV *</label>
                        <input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="***"
                          value={inlineCardForm.cvv}
                          onChange={(e) => handleInlineCardFieldChange('cvv', digitsOnly(e.target.value).slice(0, 4))}
                          className={errors.inlineCvv ? 'error' : ''}
                        />
                        {errors.inlineCvv && <span className="error-message">{errors.inlineCvv}</span>}
                      </div>
                      <div className="form-group">
                        <label>Tipo de documento *</label>
                        <select
                          value={inlineCardForm.documentType}
                          onChange={(e) => handleInlineCardFieldChange('documentType', e.target.value)}
                        >
                          <option value="CPF">CPF</option>
                          <option value="CNPJ">CNPJ</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Documento do titular *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Somente numeros"
                          value={inlineCardForm.documentNumber}
                          onChange={(e) => handleInlineCardFieldChange('documentNumber', digitsOnly(e.target.value).slice(0, 18))}
                          className={errors.inlineDocumentNumber ? 'error' : ''}
                        />
                        {errors.inlineDocumentNumber && <span className="error-message">{errors.inlineDocumentNumber}</span>}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="inline-card-grid">
                      <div className="form-group">
                        <label>Nome no cartao *</label>
                        <input
                          type="text"
                          placeholder="Nome do titular"
                          value={inlineCardForm.holderName}
                          onChange={(e) => handleInlineCardFieldChange('holderName', e.target.value)}
                          className={errors.inlineHolderName ? 'error' : ''}
                        />
                        {errors.inlineHolderName && <span className="error-message">{errors.inlineHolderName}</span>}
                      </div>
                      <div className="form-group">
                        <label>Numero do cartao *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0000 0000 0000 0000"
                          value={inlineCardForm.number}
                          onChange={(e) => handleInlineCardFieldChange('number', e.target.value)}
                          className={errors.inlineCardNumber ? 'error' : ''}
                        />
                        {errors.inlineCardNumber && <span className="error-message">{errors.inlineCardNumber}</span>}
                      </div>
                      <div className="form-group">
                        <label>Mes *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          placeholder="MM"
                          value={inlineCardForm.expMonth}
                          onChange={(e) => handleInlineCardFieldChange('expMonth', digitsOnly(e.target.value).slice(0, 2))}
                          className={errors.inlineExpMonth ? 'error' : ''}
                        />
                        {errors.inlineExpMonth && <span className="error-message">{errors.inlineExpMonth}</span>}
                      </div>
                      <div className="form-group">
                        <label>Ano *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="AAAA"
                          value={inlineCardForm.expYear}
                          onChange={(e) => handleInlineCardFieldChange('expYear', digitsOnly(e.target.value).slice(0, 4))}
                          className={errors.inlineExpYear ? 'error' : ''}
                        />
                        {errors.inlineExpYear && <span className="error-message">{errors.inlineExpYear}</span>}
                      </div>
                      <div className="form-group">
                        <label>CVV *</label>
                        <input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="***"
                          value={inlineCardForm.cvv}
                          onChange={(e) => handleInlineCardFieldChange('cvv', digitsOnly(e.target.value).slice(0, 4))}
                          className={errors.inlineCvv ? 'error' : ''}
                        />
                        {errors.inlineCvv && <span className="error-message">{errors.inlineCvv}</span>}
                      </div>
                      <div className="form-group">
                        <label>Tipo de documento *</label>
                        <select
                          value={inlineCardForm.documentType}
                          onChange={(e) => handleInlineCardFieldChange('documentType', e.target.value)}
                        >
                          <option value="CPF">CPF</option>
                          <option value="CNPJ">CNPJ</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Documento do titular *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Somente numeros"
                          value={inlineCardForm.documentNumber}
                          onChange={(e) => handleInlineCardFieldChange('documentNumber', digitsOnly(e.target.value).slice(0, 18))}
                          className={errors.inlineDocumentNumber ? 'error' : ''}
                        />
                        {errors.inlineDocumentNumber && <span className="error-message">{errors.inlineDocumentNumber}</span>}
                      </div>
                    </div>

                    <label className="inline-save-card-option">
                      <input
                        type="checkbox"
                        checked={inlineCardForm.saveCard}
                        onChange={(e) => handleInlineCardFieldChange('saveCard', e.target.checked)}
                      />
                      <span>Salvar referencia tokenizada para proxima compra (sem armazenar numero/CVV).</span>
                    </label>
                  </>
                )}

                {inlinePaymentInfo && (
                  <p className="map-helper inline-info-success">{inlinePaymentInfo}</p>
                )}
              </div>
            )}

            {paymentMethod === 'pix' && (
              <p className="map-helper checkout-payment-note">
                O QR Code/copia e cola do PIX sera gerado no checkout seguro do Mercado Pago apos confirmar o pedido.
              </p>
            )}
            {isMercadoPagoCardMethod && !isInlineSupportedForMethod && (
              <p className="map-helper checkout-payment-note">
                Voce sera redirecionado para o checkout seguro do Mercado Pago para concluir o pagamento.
              </p>
            )}
            {isInlineSupportedForMethod && (
              <p className="map-helper checkout-payment-note">
                Se houver falha tecnica no inline, o sistema redireciona automaticamente para o Checkout Pro.
              </p>
            )}
            {paymentMethod === 'voucher' && voucherEnabledForCheckout && (
              <p className="map-helper checkout-payment-note">
                Seu pedido sera enviado para autorizacao de voucher da loja antes da confirmacao final.
              </p>
            )}
            {!storeAcceptsCash && (
              <p className="map-helper checkout-payment-note">
                Esta loja nao aceita pagamento em dinheiro.
              </p>
            )}
          </section>

        </div>

        <aside className="checkout-sidebar">
          <div className="cart-summary">
            <div className="section-header">
              <ShoppingBag size={24} className="section-icon" />
              <h2>Seu Pedido</h2>
            </div>

            <div className="cart-items">
              {items.map(item => (
                <div key={item.cart_item_key || String(item.id)} className="cart-item">
                  <div className="item-details">
                    <h4>{item.name}</h4>
                    {Array.isArray(item.selected_extras) && item.selected_extras.length > 0 && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                        + {item.selected_extras.map((extra) => extra.name).filter(Boolean).join(', ')}
                      </p>
                    )}
                    {getItemRemovalNote(item) && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                        Sem: {getItemRemovalNote(item)}
                      </p>
                    )}
                    <p className="item-price">R$ {(getItemUnitPrice(item) * (item.quantity || 0)).toFixed(2).replace('.', ',')}</p>
                  </div>
                  <div className="item-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => decreaseQuantity(item.cart_item_key || item.id)}
                      disabled={item.quantity <= 1}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => increaseQuantity(item.cart_item_key || item.id)}
                    >
                      <Plus size={16} />
                    </button>
                    <button 
                      className="remove-btn"
                      onClick={() => removeItem(item.cart_item_key || item.id)}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-totals">
              <div className="total-row">
                <span>Subtotal</span>
                <span>R$ {checkoutSubtotal.toFixed(2).replace('.', ',')}</span>
              </div>
              {checkoutDiscount > 0.009 && (
                <div className="total-row discount">
                  <span>Desconto</span>
                  <span>- R$ {checkoutDiscount.toFixed(2).replace('.', ',')}</span>
                </div>
              )}
              <div className="total-row">
                <span>Taxa de entrega</span>
                <span>R$ {effectiveDeliveryFee.toFixed(2).replace('.', ',')}</span>
              </div>
              {fulfillmentType !== 'pickup' && Number.isFinite(deliveryDistanceKm) && (
                <div className="total-row">
                  <span>Distancia da loja</span>
                  <span>{formatDistanceKm(deliveryDistanceKm)}</span>
                </div>
              )}
              <div className="total-row total-final">
                <span>Total</span>
                <span>R$ {total.toFixed(2).replace('.', ',')}</span>
              </div>
            </div>

            <button 
              className="confirm-button"
              onClick={handleCheckout}
              disabled={loading || items.length === 0}
            >
              <CheckCircle size={20} className={loading ? 'confirm-icon-hidden' : ''} />
              <span className={loading ? '' : 'confirm-text-hidden'}>Processando...</span>
              <span className={loading ? 'confirm-text-hidden' : ''}>Confirmar Pedido</span>
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
